import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  Mail,
  Edit3,
  Building2,
  ExternalLink,
  Clock,
  AlertCircle,
  Search,
  Trash2,
  Check,
  X,
  Phone,
  Calendar,
  KeyRound,
  LogIn,
  BadgeCheck,
  Pencil,
  Eye,
  MousePointerClick,
  Share2,
  Users,
  MailQuestion,
  MailX,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSuppressedEmails, reasonLabel } from "@/hooks/useSuppressedEmails";
import { ShelterEditDialog } from "./ShelterEditDialog";
import { ClaimTokensSection } from "./ClaimTokensSection";
import { SuccessionProposalsSection } from "./SuccessionProposalsSection";
import { useVerifyOrganization } from "@/hooks/useOrganizationVerification";
import type { Shelter } from "@/types/shelter";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { format, formatDistanceToNow } from "date-fns";
import { pl } from "date-fns/locale";

type RecentEdit = {
  id: string;
  created_at: string;
  status: string;
  changed_fields: string[];
};

type TeamMember = {
  user_id: string;
  email: string | null;
  role: "owner" | "member";
  granted_at: string;
};

type PendingInvite = {
  id: string;
  invited_email: string;
  created_at: string;
  expires_at: string;
};

type PendingClaimRequest = {
  id: string;
  user_email: string | null;
  created_at: string;
};

type PendingJoinRequest = {
  id: string;
  requester_email: string;
  created_at: string;
};

type RejectedJoinRequest = {
  id: string;
  requester_email: string;
  created_at: string;
  reviewed_at: string | null;
};

type PendingClaimToken = {
  id: string;
  recipient_email: string | null;
  created_at: string;
  expires_at: string;
  email_sent_at: string | null;
  click_count: number;
  last_clicked_at: string | null;
};

type OrgAccount = {
  user_id: string;
  email: string | null;
  role: "owner" | "member";
  last_sign_in_at: string | null;
  shelter: {
    id: string;
    name: string;
    slug: string;
    city: string;
    organization_verified: boolean;
    completeness: number;
    completeness_total: number;
    missing_fields: string[];
  } | null;
  granted_at: string;
  edits_before_access: number;
  edits_after_access: number;
  last_edit_at: string | null;
  recent_edits: RecentEdit[];
  team: TeamMember[];
  pending_invites: PendingInvite[];
  pending_claim_requests: PendingClaimRequest[];
  pending_join_requests: PendingJoinRequest[];
  rejected_join_requests: RejectedJoinRequest[];
  pending_claim_tokens: PendingClaimToken[];
};

type ClaimRow = {
  id: string;
  user_id: string;
  shelter_id: string;
  status: string;
  contact_phone: string | null;
  justification: string;
  admin_note: string | null;
  created_at: string;
  user_email: string | null;
  shelter_name?: string;
  shelter_city?: string;
  shelter_slug?: string;
};

const normalize = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ł/g, "l");

const fmtDate = (iso: string | null | undefined) =>
  iso ? format(new Date(iso), "d MMM yyyy", { locale: pl }) : "-";

const fmtRelative = (iso: string | null | undefined) => {
  if (!iso) return "nigdy";
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: pl });
  } catch {
    return "-";
  }
};

const SuppressedBadge = ({ email }: { email: string | null | undefined }) => {
  const { data: suppressed } = useSuppressedEmails();
  if (!email) return null;
  const entry = suppressed?.get(email.trim().toLowerCase());
  if (!entry) return null;
  const label = reasonLabel(entry.reason);
  const date = fmtDate(entry.created_at);
  return (
    <Badge
      variant="outline"
      className="text-[10px] text-destructive border-destructive/40 bg-destructive/5 flex items-center gap-1"
      title={`${label} (${date}). Maile nie będą wysyłane na ten adres.`}
    >
      <MailX className="h-3 w-3" />
      Wypisany z maili
    </Badge>
  );
};

const useOrganizations = () =>
  useQuery({
    queryKey: ["admin-organizations"],
    queryFn: async (): Promise<OrgAccount[]> => {
      const { data, error } = await supabase.functions.invoke("list-shelter-organizations");
      if (error) throw error;
      return (data as { accounts: OrgAccount[] })?.accounts ?? [];
    },
  });

const usePendingClaims = () =>
  useQuery({
    queryKey: ["admin-pending-claims"],
    queryFn: async (): Promise<ClaimRow[]> => {
      const { data: rows, error } = await supabase
        .from("shelter_claim_requests")
        .select(
          "id, user_id, shelter_id, status, contact_phone, justification, admin_note, created_at, user_email",
        )
        .eq("status", "pending")
        .order("created_at", { ascending: false });
      if (error) throw error;
      const list = rows ?? [];
      if (list.length === 0) return [];

      const ids = Array.from(new Set(list.map((r) => r.shelter_id)));
      const { data: shelters } = await supabase
        .from("shelters")
        .select("id, name, city, slug")
        .in("id", ids);
      const sMap = new Map((shelters ?? []).map((s) => [s.id, s]));

      return list.map((r) => ({
        ...r,
        shelter_name: sMap.get(r.shelter_id)?.name,
        shelter_city: sMap.get(r.shelter_id)?.city,
        shelter_slug: sMap.get(r.shelter_id)?.slug,
      }));
    },
  });

const AccountCard = ({ acc }: { acc: OrgAccount }) => {
  const queryClient = useQueryClient();
  const verifyMutation = useVerifyOrganization();
  const [editingShelter, setEditingShelter] = useState<Shelter | null>(null);


  const revokeMutation = useMutation({
    mutationFn: async () => {
      if (!acc.shelter) throw new Error("Brak schroniska");
      const { error } = await supabase
        .from("shelter_owners")
        .delete()
        .eq("user_id", acc.user_id)
        .eq("shelter_id", acc.shelter.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Dostęp odebrany");
      queryClient.invalidateQueries({ queryKey: ["admin-organizations"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Nie udało się odebrać dostępu"),
  });

  // Statystyki 30 dni - lazy (RPC) per karta
  const { data: stats30 } = useQuery({
    queryKey: ["owner-shelter-stats", acc.shelter?.id],
    queryFn: async () => {
      if (!acc.shelter?.id) return null;
      const { data, error } = await supabase.rpc("get_owner_shelter_stats", {
        _shelter_id: acc.shelter.id,
        _days: 30,
      });
      if (error) throw error;
      return data as {
        views: number;
        shares: number;
        phone: number;
        email: number;
        website: number;
        downloads: number;
      } | null;
    },
    enabled: !!acc.shelter?.id,
    staleTime: 60_000,
  });

  const handleOpenEdit = async () => {
    if (!acc.shelter?.id) return;
    const { data, error } = await supabase
      .from("shelters")
      .select("*")
      .eq("id", acc.shelter.id)
      .single();
    if (error || !data) {
      toast.error("Nie udało się wczytać schroniska");
      return;
    }
    setEditingShelter(data as unknown as Shelter);
  };

  const handleVerify = () => {
    if (!acc.shelter?.id) return;
    verifyMutation.mutate(
      { shelterId: acc.shelter.id, verified: true },
      {
        onSuccess: () => {
          toast.success("Zweryfikowano organizację");
          queryClient.invalidateQueries({ queryKey: ["admin-organizations"] });
        },
        onError: (e: any) => toast.error(e?.message ?? "Błąd weryfikacji"),
      },
    );
  };

  const completenessPct = acc.shelter
    ? Math.round((acc.shelter.completeness / acc.shelter.completeness_total) * 100)
    : 0;
  const completenessColor =
    completenessPct === 100
      ? "bg-emerald-500"
      : completenessPct >= 60
      ? "bg-amber-500"
      : "bg-destructive";

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
              <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="truncate">{acc.shelter?.name ?? "(brak schroniska)"}</span>
              {acc.shelter?.organization_verified ? (
                <Badge variant="outline" className="text-xs text-primary border-primary/40 bg-primary/5 flex items-center gap-1">
                  <BadgeCheck className="h-3 w-3" />
                  Zweryfikowane
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs text-amber-700 border-amber-500/40 bg-amber-50 dark:text-amber-400 dark:bg-amber-950/30">
                  Niezweryfikowane
                </Badge>
              )}
            </CardTitle>
            <CardDescription className="mt-1.5 space-y-1">
              <div className="flex items-center gap-1.5 text-xs flex-wrap">
                <Mail className="h-3 w-3 shrink-0" />
                <span className="break-all">{acc.email ?? "(brak emaila)"}</span>
                <SuppressedBadge email={acc.email} />
              </div>
              <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Dostęp od {fmtDate(acc.granted_at)}
                </span>
                <span className="flex items-center gap-1">
                  <LogIn className="h-3 w-3" />
                  Ostatnie logowanie:{" "}
                  <span className="font-medium text-foreground">
                    {acc.last_sign_in_at ? fmtDate(acc.last_sign_in_at) : "nigdy"}
                  </span>
                </span>
                {acc.shelter && <span>{acc.shelter.city}</span>}
              </div>
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Statystyki edycji - kompaktowy pasek */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Edit3 className="h-3 w-3" />
            Edycje: <span className="font-semibold text-foreground">{acc.edits_before_access}</span>
            <span className="text-muted-foreground/70">przed</span>
            <span className="text-muted-foreground/40">/</span>
            <span className="font-semibold text-foreground">{acc.edits_after_access}</span>
            <span className="text-muted-foreground/70">po dostępie</span>
          </span>
          <span className="text-muted-foreground/40">·</span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Ostatnia: <span className="font-medium text-foreground">{fmtRelative(acc.last_edit_at)}</span>
          </span>
        </div>

        {/* Zespół schroniska - zawsze widoczny */}
        <div className="rounded-md border bg-muted/30 p-2.5 space-y-2">
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" />
              Zespół ({1 + acc.team.length}/3)
            </span>
            {acc.pending_invites.length > 0 && (
              <span className="flex items-center gap-1 text-amber-700 dark:text-amber-400">
                <MailQuestion className="h-3 w-3" />
                {acc.pending_invites.length}{" "}
                {acc.pending_invites.length === 1 ? "zaproszenie" : "zaproszenia"} oczekuje
              </span>
            )}
          </div>

          <ul className="space-y-1 text-xs">
            <li className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 min-w-0">
                <Mail className="h-3 w-3 shrink-0 text-muted-foreground" />
                <span className="truncate">{acc.email ?? "(brak emaila)"}</span>
              </span>
              <Badge variant="outline" className="text-[10px] shrink-0">
                Właściciel
              </Badge>
            </li>
            {acc.team.map((m) => (
              <li key={m.user_id} className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 min-w-0">
                  <Mail className="h-3 w-3 shrink-0 text-muted-foreground" />
                  <span className="truncate">{m.email ?? "(brak emaila)"}</span>
                </span>
                <Badge variant="outline" className="text-[10px] shrink-0">
                  {m.role === "owner" ? "Właściciel" : "Członek"}
                </Badge>
              </li>
            ))}
            {acc.team.length === 0 && acc.pending_invites.length === 0 && (
              <li className="text-[11px] text-muted-foreground italic pl-4.5">
                Brak innych członków zespołu
              </li>
            )}
          </ul>

            {acc.pending_invites.length > 0 && (
              <ul className="space-y-1 text-xs pt-1 border-t border-border/50">
                {acc.pending_invites.map((inv) => {
                  const expired = new Date(inv.expires_at).getTime() < Date.now();
                  return (
                    <li key={inv.id} className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-1.5 min-w-0">
                        <MailQuestion className="h-3 w-3 shrink-0 text-amber-600 dark:text-amber-400" />
                        <span className="truncate text-muted-foreground">{inv.invited_email}</span>
                      </span>
                      <Badge
                        variant="outline"
                        className={`text-[10px] shrink-0 ${
                          expired
                            ? "border-destructive/40 text-destructive"
                            : "border-amber-500/40 text-amber-700 dark:text-amber-400"
                        }`}
                      >
                        {expired ? "Wygasło" : `Wysłane ${fmtRelative(inv.created_at)}`}
                      </Badge>
                    </li>
                  );
                })}
              </ul>
            )}
        </div>

        {/* Oczekujące prośby/linki o dostęp do schroniska */}
        {(acc.pending_claim_tokens.length > 0 ||
          acc.pending_claim_requests.length > 0 ||
          acc.pending_join_requests.length > 0) && (
          <div className="rounded-md border border-amber-500/40 bg-amber-50 dark:bg-amber-950/20 p-2.5 space-y-2">
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-amber-800 dark:text-amber-300">
              <KeyRound className="h-3 w-3" />
              Oczekujące prośby o dostęp
            </div>
            <ul className="space-y-1 text-xs">
              {acc.pending_claim_tokens.map((t) => {
                const expired = new Date(t.expires_at).getTime() < Date.now();
                return (
                  <li key={`tok-${t.id}`} className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 min-w-0">
                      <KeyRound className="h-3 w-3 shrink-0 text-amber-600 dark:text-amber-400" />
                      <span className="truncate">
                        Magic link: <span className="text-muted-foreground">{t.recipient_email ?? "(bez adresata)"}</span>
                      </span>
                    </span>
                    <Badge
                      variant="outline"
                      className={`text-[10px] shrink-0 ${
                        expired
                          ? "border-destructive/40 text-destructive"
                          : t.click_count > 0
                          ? "border-emerald-500/40 text-emerald-700 dark:text-emerald-400"
                          : "border-amber-500/40 text-amber-700 dark:text-amber-400"
                      }`}
                      title={`Wysłany ${fmtRelative(t.email_sent_at ?? t.created_at)} · kliknięć: ${t.click_count}`}
                    >
                      {expired
                        ? "Wygasł"
                        : t.click_count > 0
                        ? `Kliknięty (${t.click_count})`
                        : `Czeka na kliknięcie`}
                    </Badge>
                  </li>
                );
              })}
              {acc.pending_claim_requests.map((c) => (
                <li key={`cr-${c.id}`} className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 min-w-0">
                    <MailQuestion className="h-3 w-3 shrink-0 text-amber-600 dark:text-amber-400" />
                    <span className="truncate">
                      Wniosek o przejęcie: <span className="text-muted-foreground">{c.user_email ?? "(brak emaila)"}</span>
                    </span>
                  </span>
                  <Badge variant="outline" className="text-[10px] shrink-0 border-amber-500/40 text-amber-700 dark:text-amber-400">
                    Czeka na akceptację admina · {fmtRelative(c.created_at)}
                  </Badge>
                </li>
              ))}
              {acc.pending_join_requests.map((j) => (
                <li key={`jr-${j.id}`} className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 min-w-0">
                    <Users className="h-3 w-3 shrink-0 text-amber-600 dark:text-amber-400" />
                    <span className="truncate">
                      Prośba o dołączenie do zespołu: <span className="text-muted-foreground">{j.requester_email}</span>
                    </span>
                  </span>
                  <Badge variant="outline" className="text-[10px] shrink-0 border-amber-500/40 text-amber-700 dark:text-amber-400">
                    Czeka na akceptację właściciela · {fmtRelative(j.created_at)}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Historia odrzuconych próśb o dołączenie do zespołu */}
        {acc.rejected_join_requests && acc.rejected_join_requests.length > 0 && (
          <div className="rounded-md border border-muted bg-muted/30 p-2.5 space-y-2">
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
              <MailX className="h-3 w-3" />
              Odrzucone prośby o dołączenie do zespołu ({acc.rejected_join_requests.length})
            </div>
            <ul className="space-y-1 text-xs">
              {acc.rejected_join_requests.map((j) => (
                <li key={`rjr-${j.id}`} className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 min-w-0">
                    <X className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <span className="truncate text-muted-foreground">{j.requester_email}</span>
                  </span>
                  <Badge variant="outline" className="text-[10px] shrink-0 border-muted-foreground/30 text-muted-foreground">
                    Odrzucona {fmtRelative(j.reviewed_at ?? j.created_at)}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Kompletność profilu */}
        {acc.shelter && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="space-y-1 cursor-help">
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>Kompletność profilu</span>
                    <span className="font-medium text-foreground">
                      {acc.shelter.completeness}/{acc.shelter.completeness_total} ({completenessPct}%)
                    </span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full ${completenessColor} transition-all`}
                      style={{ width: `${completenessPct}%` }}
                    />
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                {acc.shelter.missing_fields.length === 0 ? (
                  <p className="text-xs">Profil w pełni uzupełniony 🎉</p>
                ) : (
                  <div className="text-xs space-y-1">
                    <p className="font-medium">Brakuje:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {acc.shelter.missing_fields.map((f) => (
                        <li key={f}>{f}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {/* Statystyki 30 dni */}
        {acc.shelter && (
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-md border bg-card p-2">
              <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Eye className="h-3 w-3" /> Wyświetlenia
              </div>
              <div className="text-base font-semibold mt-0.5">{stats30?.views ?? "-"}</div>
            </div>
            <div className="rounded-md border bg-card p-2">
              <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                <MousePointerClick className="h-3 w-3" /> Kontakty
              </div>
              <div className="text-base font-semibold mt-0.5">
                {stats30 ? stats30.phone + stats30.email + stats30.website : "-"}
              </div>
            </div>
            <div className="rounded-md border bg-card p-2">
              <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Share2 className="h-3 w-3" /> Udostępnienia
              </div>
              <div className="text-base font-semibold mt-0.5">{stats30?.shares ?? "-"}</div>
            </div>
          </div>
        )}
        <p className="text-[10px] text-muted-foreground -mt-2">Statystyki za ostatnie 30 dni</p>


        {/* Akcje */}
        <div className="flex flex-wrap gap-2 pt-1">
          {acc.shelter && (
            <Button asChild size="sm" variant="outline">
              <a
                href={`https://gdzieschronisko.pl/schronisko/${acc.shelter.slug}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                Profil
              </a>
            </Button>
          )}
          {acc.shelter && (
            <Button size="sm" variant="outline" onClick={handleOpenEdit}>
              <Pencil className="h-3.5 w-3.5 mr-1.5" />
              Edytuj profil
            </Button>
          )}
          {acc.shelter && !acc.shelter.organization_verified && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleVerify}
              disabled={verifyMutation.isPending}
              className="text-primary border-primary/40"
            >
              {verifyMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <BadgeCheck className="h-3.5 w-3.5 mr-1.5" />
              )}
              Zweryfikuj
            </Button>
          )}
          {acc.email && (
            <Button asChild size="sm" variant="outline">
              <a href={`mailto:${acc.email}`}>
                <Mail className="h-3.5 w-3.5 mr-1.5" />
                Napisz
              </a>
            </Button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Odbierz dostęp
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Odebrać dostęp?</AlertDialogTitle>
                <AlertDialogDescription>
                  Konto {acc.email} straci możliwość edycji schroniska
                  {" "}
                  <strong>{acc.shelter?.name}</strong>. Konto użytkownika nie zostanie
                  usunięte. Operację można cofnąć przez ponowne przyznanie dostępu (zatwierdzenie
                  nowego wniosku).
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Anuluj</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => revokeMutation.mutate()}
                  disabled={revokeMutation.isPending}
                >
                  {revokeMutation.isPending && (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  )}
                  Odbierz
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
      <ShelterEditDialog
        shelter={editingShelter}
        open={!!editingShelter}
        onOpenChange={(open) => { if (!open) setEditingShelter(null); }}
      />
    </Card>
  );
};

const ClaimCard = ({ row }: { row: ClaimRow }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [rejectNote, setRejectNote] = useState("");

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-pending-claims"] });
    queryClient.invalidateQueries({ queryKey: ["admin-organizations"] });
  };

  const approve = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("shelter_claim_requests")
        .update({
          status: "approved",
          reviewed_by: user?.id ?? null,
          reviewed_at: new Date().toISOString(),
          admin_note: null,
        })
        .eq("id", row.id);
      if (error) throw error;
      if (row.user_email) {
        try {
          await supabase.functions.invoke("send-transactional-email", {
            body: {
              templateName: "claim-request-approved",
              recipientEmail: row.user_email,
              idempotencyKey: `claim-approved-${row.id}`,
              templateData: {
                shelterName: row.shelter_name,
                panelUrl: "https://gdzieschronisko.pl/panel",
              },
            },
          });
        } catch (e) {
          console.warn("approval email failed", e);
        }
      }
    },
    onSuccess: () => {
      toast.success("Wniosek zatwierdzony, dostęp przyznany");
      invalidate();
    },
    onError: (e: any) => toast.error(e?.message ?? "Nie udało się zatwierdzić"),
  });

  const reject = useMutation({
    mutationFn: async (note: string) => {
      const { error } = await supabase
        .from("shelter_claim_requests")
        .update({
          status: "rejected",
          reviewed_by: user?.id ?? null,
          reviewed_at: new Date().toISOString(),
          admin_note: note || null,
        })
        .eq("id", row.id);
      if (error) throw error;
      if (row.user_email) {
        try {
          await supabase.functions.invoke("send-transactional-email", {
            body: {
              templateName: "claim-request-rejected",
              recipientEmail: row.user_email,
              idempotencyKey: `claim-rejected-${row.id}`,
              templateData: { shelterName: row.shelter_name, reason: note || undefined },
            },
          });
        } catch (e) {
          console.warn("rejection email failed", e);
        }
      }
    },
    onSuccess: () => {
      toast.success("Wniosek odrzucony");
      setRejecting(false);
      setRejectNote("");
      invalidate();
    },
    onError: (e: any) => toast.error(e?.message ?? "Nie udało się odrzucić"),
  });

  return (
    <Card className="border-l-4 border-l-orange-400">
      <CardHeader className="pb-3">
        <div className="min-w-0">
          <CardTitle className="text-base flex items-center gap-2 flex-wrap">
            <KeyRound className="h-4 w-4 text-orange-600 shrink-0" />
            <span className="truncate">{row.shelter_name ?? "Nieznane schronisko"}</span>
            <Badge variant="outline" className="border-orange-300 text-orange-700">
              Oczekuje
            </Badge>
          </CardTitle>
          <CardDescription className="mt-1.5 space-y-1">
            {row.user_email && (
              <div className="flex items-center gap-1.5 text-xs">
                <Mail className="h-3 w-3 shrink-0" />
                <span className="break-all">{row.user_email}</span>
              </div>
            )}
            <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
              {row.shelter_city && <span>{row.shelter_city}</span>}
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {format(new Date(row.created_at), "d MMM yyyy, HH:mm", { locale: pl })}
              </span>
              {row.shelter_slug && (
                <a
                  href={`https://gdzieschronisko.pl/schronisko/${row.shelter_slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-primary hover:underline"
                >
                  <ExternalLink className="h-3 w-3" /> Profil
                </a>
              )}
            </div>
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {row.contact_phone && (
          <div className="text-sm flex items-center gap-2">
            <Phone className="h-3.5 w-3.5 text-muted-foreground" />
            <a href={`tel:${row.contact_phone}`} className="hover:underline">
              {row.contact_phone}
            </a>
          </div>
        )}
        <div className="text-sm">
          <div className="font-medium text-xs text-muted-foreground mb-1">Uzasadnienie:</div>
          <div className="whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-sm">
            {row.justification}
          </div>
        </div>

        {rejecting ? (
          <div className="space-y-2 border-t pt-3">
            <Textarea
              placeholder="Powód odrzucenia (opcjonalnie - widoczny dla wnioskującego)"
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              maxLength={500}
              rows={3}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="destructive"
                onClick={() => reject.mutate(rejectNote)}
                disabled={reject.isPending}
              >
                {reject.isPending && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                Potwierdź odrzucenie
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setRejecting(false);
                  setRejectNote("");
                }}
              >
                Anuluj
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2 border-t pt-3">
            <Button
              size="sm"
              onClick={() => approve.mutate()}
              disabled={approve.isPending}
            >
              {approve.isPending ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <Check className="h-3 w-3 mr-1" />
              )}
              Zatwierdź i przyznaj dostęp
            </Button>
            <Button size="sm" variant="outline" onClick={() => setRejecting(true)}>
              <X className="h-3 w-3 mr-1" /> Odrzuć
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const OrganizationsTab = () => {
  const { data: accounts, isLoading: accountsLoading, error: accountsError } =
    useOrganizations();
  const { data: claims, isLoading: claimsLoading } = usePendingClaims();
  const [search, setSearch] = useState("");

  const q = normalize(search.trim());

  const filteredAccounts = useMemo(() => {
    if (!accounts) return [];
    if (!q) return accounts;
    return accounts.filter((a) => {
      const hay = normalize(
        [a.shelter?.name ?? "", a.shelter?.city ?? "", a.email ?? ""].join(" "),
      );
      return hay.includes(q);
    });
  }, [accounts, q]);

  const filteredClaims = useMemo(() => {
    if (!claims) return [];
    if (!q) return claims;
    return claims.filter((c) => {
      const hay = normalize(
        [c.shelter_name ?? "", c.shelter_city ?? "", c.user_email ?? ""].join(" "),
      );
      return hay.includes(q);
    });
  }, [claims, q]);

  if (accountsLoading || claimsLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (accountsError) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-destructive">
          <AlertCircle className="h-5 w-5 mx-auto mb-2" />
          Nie udało się załadować listy organizacji.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Wyszukiwarka */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          placeholder="Szukaj po nazwie schroniska, mieście lub emailu"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Sekcja: Oczekujące wnioski */}
      {filteredClaims.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="font-display font-semibold text-base">
              Oczekujące wnioski o dostęp
            </h3>
            <Badge variant="outline" className="border-orange-300 text-orange-700">
              {filteredClaims.length}
            </Badge>
          </div>
          <div className="space-y-3">
            {filteredClaims.map((row) => (
              <ClaimCard key={row.id} row={row} />
            ))}
          </div>
        </section>
      )}

      {/* Sekcja: Aktywne konta */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <h3 className="font-display font-semibold text-base">Aktywne konta organizacji</h3>
          <Badge variant="secondary">{filteredAccounts.length}</Badge>
        </div>

        {filteredAccounts.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              {q ? "Brak kont pasujących do wyszukiwania." : "Brak aktywnych kont organizacji."}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredAccounts.map((acc) => (
              <AccountCard key={acc.user_id} acc={acc} />
            ))}
          </div>
        )}
      </section>

      {/* Sekcja: Propozycje następcy po rezygnacji */}
      <SuccessionProposalsSection />

      {/* Sekcja: Linki do przejęcia */}
      <ClaimTokensSection />
    </div>
  );
};
